import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const TenantContext = createContext(null);

/**
 * Tenant Context Provider.
 * Exposes current tenant details, tenantId, and super admin status based on AuthContext user.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {React.ReactNode} props.children - Child elements wrapping the provider.
 * @returns {JSX.Element} TenantContext Provider component.
 */
export const TenantProvider = ({ children }) => {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(user?.tenant || null);
  const [tenantId, setTenantId] = useState(user?.tenantId || "tenant-default-001");

  useEffect(() => {
    if (user) {
      setTenant(user.tenant || null);
      setTenantId(user.tenantId || "tenant-default-001");
    } else {
      setTenant(null);
      setTenantId("tenant-default-001");
    }
  }, [user]);

  return (
    <TenantContext.Provider value={{ tenant, tenantId, isSuperAdmin: user?.role === "super_admin" }}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Custom React hook to consume the Tenant Context.
 *
 * @function useTenant
 * @returns {Object} Tenant context object containing tenant, tenantId, and isSuperAdmin flag.
 * @throws {Error} If called outside of a TenantProvider.
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};
